import React, { useEffect, useState, useMemo } from "react";
import NetworkCanvas, { NetworkRows } from "./NetworkCanvas";
import DeviceDrawer from "./DeviceDrawer";
import { SiteSoftware } from "./UpdatesPanel";
import { fmtBytes, fmtNum, fmtGoDuration, ago } from "../utils/formatters";
import { WanLabel } from "./WanNames";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import usePhoneLayout from "../hooks/usePhoneLayout";
import {
  FiMonitor,
  FiActivity,
  FiHardDrive,
  FiCpu,
  FiWifi,
  FiDatabase,
  FiLayers,
  FiEyeOff,
  FiEye,
  FiClock,
} from "react-icons/fi";

// ── Summary stat card ────────────────────────────────────────────────────────

function SummaryCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="dash-stat" style={{ "--accent": accent || "#6366f1" }}>
      <div className="dash-stat-icon">
        <Icon size={20} />
      </div>
      <div className="dash-stat-body">
        <div className="dash-stat-label">{label}</div>
        <div className="dash-stat-value">{value}</div>
        {sub && <div className="dash-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

// ── System detail mini-card ──────────────────────────────────────────────────

// The agent reports its heap in megabytes; shown in whatever unit it reaches.
const heapText = (mb) => (mb ? fmtBytes(mb * 1048576) : "—");

// tone colours a reading that is good or bad news. It is text, never a pill:
// a filled capsule on a card reads as a button, and nothing here is one.
function MiniCard({ label, value, tone }) {
  return (
    <div className="dash-mini-card">
      <div className="dash-mini-label">{label}</div>
      <div className={`dash-mini-value ${tone ? `is-${tone}` : ""}`}>{value}</div>
    </div>
  );
}

// ── WAN link compact card ────────────────────────────────────────────────────

function WANCompact({ wan }) {
  const isUp = wan.active !== false;
  return (
    <div className="dash-wan-compact">
      <div className="dash-wan-name">
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: isUp ? "#22c55e" : "#ef4444",
            marginRight: 6,
            verticalAlign: "middle",
          }}
        />
        <WanLabel iface={wan.name} />
      </div>
      <div className="dash-wan-stats">
        <span>↑ {fmtBytes(wan.tx_bytes)}</span>
        <span>↓ {fmtBytes(wan.rx_bytes)}</span>
        <span
          className={`dash-wan-errors ${wan.errors > 0 ? "has-errors" : ""}`}
        >
          {wan.errors > 0 ? `${fmtNum(wan.errors)} err` : "0 err"}
        </span>
      </div>
      <div className="dash-wan-meta">
        Up: {ago(wan.last_up)} · Down: {ago(wan.last_down)}
      </div>
    </div>
  );
}

// ── Main DashboardView ───────────────────────────────────────────────────────

// A phone answers /diagnostics with its links and its tunnel, and has no
// devices, host or classifier behind it, so it gets the cards that describe it
// instead of three that would read as nothing running.
// serverApiUrl, given only where the Weavian server is behind the console,
// adds the box's software and whether it is up to date under the title.
export default function DashboardView({ nodeApiUrl, selectedNode, phone = false, serverApiUrl, onOpenUpdates }) {
  // The network as rows where the screen is too narrow for its drawing.
  const phoneLayout = usePhoneLayout();
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastFetch, setLastFetch] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  // A phone's details are its links and its tunnel, which are the point of
  // its dashboard, so they start open.
  const [systemExpanded, setSystemExpanded] = useState(phone);
  const [hideStale, setHideStale] = useState(false);

  // Poll diagnostics
  useEffect(() => {
    if (!selectedNode) {
      setDiag(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setDiag(null);
    setLoading(true);

    const doFetch = async () => {
      const url = nodeApiUrl("/diagnostics");
      if (!url) return;
      try {
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data = await res.json();
        if (!controller.signal.aborted) {
          setDiag(data);
          setError("");
          setLastFetch(new Date());
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        if (!controller.signal.aborted)
          setError(err.message || "Failed to fetch diagnostics");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    doFetch();
    const timer = setInterval(doFetch, 5000);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [selectedNode, nodeApiUrl]);

  // Keep the selected device in sync with fresh diagnostics data
  useEffect(() => {
    if (!selectedDevice || !diag?.devices) return;
    // By MAC when the device has one, else by address: comparing two missing
    // MACs matched any other device without one, and the drawer jumped to it.
    const fresh = diag.devices.find((d) =>
      selectedDevice.mac ? d.mac === selectedDevice.mac : d.ip === selectedDevice.ip
    );
    if (fresh) {
      setSelectedDevice(fresh);
    }
  }, [diag]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stats
  const deviceCount = diag?.devices?.length || 0;
  const activeCount = useMemo(() => {
    if (!diag?.devices) return 0;
    return diag.devices.filter((d) => (d.active_seconds ?? 0) <= 60).length;
  }, [diag]);

  const tunnelTX = fmtBytes(diag?.tunnel?.tx_bytes);
  const tunnelRX = fmtBytes(diag?.tunnel?.rx_bytes);

  const wanList = useMemo(() => {
    if (!diag?.wans) return [];
    return Object.values(diag.wans).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
  }, [diag]);

  const sys = diag?.system;
  const tc = diag?.tc_classifier;
  const carrying = wanList.filter((w) => w.active).length;

  // ── No node selected state ──
  if (!selectedNode) {
    return (
      <div className="dash-empty-state">
        <div className="dash-empty-icon">
          <FiMonitor size={48} />
        </div>
        <h2>Welcome to Weavian</h2>
        <p>Choose a box at the top right to see its network.</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* ── Page header ── */}
      <div className="dash-header">
        <div>
          <h1 className="dash-page-title">Dashboard</h1>
          <p className="dash-page-sub">
            Network overview for <strong>{selectedNode}</strong>
            {lastFetch && (
              <span className="dash-refresh-ts">
                {" "}
                · Updated {lastFetch.toLocaleTimeString()}
              </span>
            )}
          </p>
          {serverApiUrl && <SiteSoftware serverApiUrl={serverApiUrl} node={selectedNode} onOpen={onOpenUpdates} />}
        </div>
      </div>

      {error && <div className="dash-error">{error}</div>}

      {/* ── Summary strip ── */}
      <div className="dash-stats-row">
        {phone ? (
          <SummaryCard
            icon={FiWifi}
            label="Links Carrying"
            value={diag ? `${carrying} of ${wanList.length}` : loading ? "Loading…" : "—"}
            sub={wanList.map((w) => w.name).join(" · ")}
            accent="#6366f1"
          />
        ) : (
          <SummaryCard
            icon={FiMonitor}
            label="Connected Devices"
            value={activeCount}
            sub={`${deviceCount} total`}
            accent="#6366f1"
          />
        )}
        <SummaryCard
          icon={FiActivity}
          label="Tunnel Traffic"
          value={`↑ ${tunnelTX}`}
          sub={`↓ ${tunnelRX}`}
          accent="#22d3ee"
        />
        {phone ? (
          <SummaryCard
            icon={FiClock}
            label="Connected For"
            value={diag?.uptime ? fmtGoDuration(diag.uptime) : (loading ? "Loading…" : "—")}
            sub="this session"
            accent="#22c55e"
          />
        ) : (
          <SummaryCard
            icon={FiCpu}
            label="Running For"
            value={sys?.uptime ? fmtGoDuration(sys.uptime) : (loading ? "Loading…" : "—")}
            sub={sys ? `${sys.num_cpu} CPUs · ${heapText(sys.heap_mb)} heap` : ""}
            accent="#22c55e"
          />
        )}
        {!phone && (
          <SummaryCard
            icon={FiLayers}
            label="Traffic Classifier"
            value={tc?.alive ? "Running" : loading ? "Loading…" : "Stopped"}
            sub={tc ? `${fmtNum(tc.flows)} flows · ${fmtNum(tc.classified)} classified` : ""}
            accent={tc?.alive ? "#22c55e" : "#ef4444"}
          />
        )}
      </div>

      {/* ── Network Canvas: the devices behind an edge, which a phone has none of ── */}
      {!phone && <div className="dash-canvas-section">
        <div className="dash-section-header">
          <h2 className="dash-section-title">{phoneLayout ? "Lines and devices" : "Network Topology"}</h2>
          <div className="dash-canvas-controls">
            <span className="dash-device-count">
              {activeCount} active
              {deviceCount - activeCount > 0 &&
                ` · ${deviceCount - activeCount} idle`}
            </span>
            {deviceCount - activeCount > 0 && (
              <button
                type="button"
                className="dash-hide-stale-btn"
                onClick={() => setHideStale((v) => !v)}
                aria-pressed={hideStale}
                title={hideStale
                  ? "Show the devices not seen in the last minute"
                  : "Hide the devices not seen in the last minute"}
              >
                {hideStale ? <FiEye size={14} /> : <FiEyeOff size={14} />}
                {hideStale ? "Show idle devices" : "Hide idle devices"}
              </button>
            )}
          </div>
        </div>
        {phoneLayout ? (
          <NetworkRows
            devices={diag?.devices || []}
            onDeviceClick={(d) => setSelectedDevice(d)}
            wans={diag?.wans}
            hideStale={hideStale}
          />
        ) : (
          <NetworkCanvas
            devices={diag?.devices || []}
            selectedNode={selectedNode}
            onDeviceClick={(d) => setSelectedDevice(d)}
            wans={diag?.wans}
            hideStale={hideStale}
          />
        )}
      </div>}

      {/* ── System Details (collapsible) ── */}
      <div className="dash-system-section">
        <button
          className="dash-system-toggle"
          onClick={() => setSystemExpanded((v) => !v)}
        >
          <span className="dash-system-toggle-label">
            <FiDatabase size={16} />
            {phone ? "Links and Tunnel" : "System Details"}
          </span>
          <span
            className={`dash-chevron ${systemExpanded ? "open" : ""}`}
          >
            ▾
          </span>
        </button>

        {systemExpanded && (
          <div className="dash-system-content">
            {/* System health */}
            {sys && (
              <div className="dash-system-group">
                <h3 className="dash-group-title">
                  <FiCpu size={14} /> System Health
                </h3>
                <div className="dash-mini-grid">
                  <MiniCard label="Uptime" value={fmtGoDuration(sys.uptime)} />
                  <MiniCard label="Goroutines" value={fmtNum(sys.goroutines)} />
                  <MiniCard label="Heap" value={heapText(sys.heap_mb)} />
                  <MiniCard label="CPUs" value={fmtNum(sys.num_cpu)} />
                  <MiniCard
                    label="IP Forward"
                    value={sys.ip_forward ? "Enabled" : "Disabled"}
                    tone={sys.ip_forward ? "ok" : "bad"}
                  />
                  <MiniCard label="Restarts" value={fmtNum(sys.restart_count)} />
                </div>
              </div>
            )}

            {/* Tunnel */}
            {diag?.tunnel && (
              <div className="dash-system-group">
                <h3 className="dash-group-title">
                  <FiHardDrive size={14} /> Tunnel
                </h3>
                <div className="dash-mini-grid">
                  <MiniCard
                    label="TX"
                    value={fmtBytes(diag.tunnel.tx_bytes)}
                  />
                  <MiniCard
                    label="RX"
                    value={fmtBytes(diag.tunnel.rx_bytes)}
                  />
                  <MiniCard
                    label="TX Pkts"
                    value={fmtNum(diag.tunnel.tx_packets)}
                  />
                  <MiniCard
                    label="RX Pkts"
                    value={fmtNum(diag.tunnel.rx_packets)}
                  />
                  <MiniCard
                    label="Errors"
                    value={fmtNum(diag.tunnel.errors)}
                    tone={diag.tunnel.errors > 0 ? "bad" : "ok"}
                  />
                </div>
              </div>
            )}

            {/* WAN links */}
            {wanList.length > 0 && (
              <div className="dash-system-group">
                <h3 className="dash-group-title">
                  <FiWifi size={14} /> WAN Links
                </h3>
                <div className="dash-wan-list">
                  {wanList.map((w) => (
                    <WANCompact key={w.name} wan={w} />
                  ))}
                </div>
              </div>
            )}

            {/* DHCP leases count */}
            {diag?.dhcp_leases && diag.dhcp_leases.length > 0 && (
              <div className="dash-system-group">
                <h3 className="dash-group-title">
                  <FiDatabase size={14} /> DHCP Leases
                </h3>
                <div className="dash-dhcp-list">
                  {diag.dhcp_leases.map((l, i) => (
                    <div key={`${l.ip}-${i}`} className="dash-dhcp-item">
                      <span className="dash-dhcp-ip">{l.ip}</span>
                      <span className="dash-dhcp-mac">{l.mac || "—"}</span>
                      <span className="dash-dhcp-host">
                        {l.hostname || "—"}
                      </span>
                      <span className="dash-dhcp-exp">
                        {l.expiry
                          ? new Date(l.expiry * 1000).toLocaleString()
                          : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Device detail drawer ── */}
      {selectedDevice && (
        <DeviceDrawer
          device={selectedDevice}
          nodeId={selectedNode}
          onClose={() => setSelectedDevice(null)}
          classifierMode={diag?.classifier_mode || null}
        />
      )}
    </div>
  );
}
